import { Suspense } from 'react'
import { Navigate, RouteObject } from 'react-router-dom'
import { ProtectedRoute } from '@/shared/components/ui'
import { ErrorPage } from './ErrorPage'
import { lazyRetry } from '@/shared/utils/lazyRetry'

// 懒加载页面组件
const LoginPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.LoginPage })))
const RegisterPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.RegisterPage })))
const OAuthCallbackPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.OAuthCallbackPage })))
const ProfilePage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.ProfilePage })))
const ApiKeysPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.ApiKeysPage })))
const TeamsPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.TeamsPage })))
const UserManagementPage = lazyRetry(() => import('@/modules/user-management').then(m => ({ default: m.UserManagementPage })))
const OAuthProvidersPage = lazyRetry(() => import('@/modules/user-management/pages/OAuthProvidersPage'))
const OAuthProviderFormPage = lazyRetry(() => import('@/modules/user-management/pages/OAuthProviderFormPage'))
const UserFeedbackPage = lazyRetry(() => import('@/modules/user-management/pages/UserFeedbackPage'))
const AdminFeedbackManagementPage = lazyRetry(() => import('@/modules/user-management/pages/AdminFeedbackManagementPage'))
const DashboardPage = lazyRetry(() => import('@/modules/dashboard').then(m => ({ default: m.DashboardPage })))
const SubProjectsPage = lazyRetry(() => import('@/modules/project-management').then(m => ({ default: m.SubProjectsPage })))
const ProjectsPage = lazyRetry(() => import('@/modules/project-management').then(m => ({ default: m.ProjectsPage })))
const ProjectDetailPage = lazyRetry(() => import('@/modules/project-management').then(m => ({ default: m.ProjectDetailPage })))
const FileManagementPage = lazyRetry(() => import('@/modules/file-management').then(m => ({ default: m.FileManagementPage })))
const FileDetailPage = lazyRetry(() => import('@/modules/file-management').then(m => ({ default: m.FileDetailPage })))
const ChunkDetailPage = lazyRetry(() => import('@/modules/file-management').then(m => ({ default: m.ChunkDetailPage })))
const SearchPage = lazyRetry(() => import('@/modules/file-management').then(m => ({ default: m.SearchPage })))

// 检测配置模块
const DetectionConfigPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.DetectionConfigPage })))
const DetectionRulesPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.DetectionRulesPage })))
const DetectionStrategiesPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.DetectionStrategiesPage })))
const ModelConfigsPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.ModelConfigsPage })))
const ModelTestPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.ModelTestPage })))
const CategoryManagementPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.CategoryManagementPage })))
const SshTargetManagementPage = lazyRetry(() => import('@/modules/detection-config/pages/SshTargetManagementPage'))
const ModelTagManagementPage = lazyRetry(() => import('@/modules/detection-config/pages/ModelTagManagementPage'))
const LinkVersionsPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.LinkVersionsPage })))
const LinkVersionFormPage = lazyRetry(() => import('@/modules/detection-config').then(m => ({ default: m.LinkVersionFormPage })))
const SensitiveWordsPage = lazyRetry(() => import('@/modules/detection-config/pages/SensitiveWordsPage'))
const SensitiveWordFormPage = lazyRetry(() => import('@/modules/detection-config/pages/SensitiveWordFormPage'))

// Plugin 仓库模块
const PluginListPage = lazyRetry(() => import('@/modules/plugin-repo').then(m => ({ default: m.PluginListPage })))
const PluginDetailPage = lazyRetry(() => import('@/modules/plugin-repo').then(m => ({ default: m.PluginDetailPage })))
const PluginFormPage = lazyRetry(() => import('@/modules/plugin-repo').then(m => ({ default: m.PluginFormPage })))
const PluginAuditPanel = lazyRetry(() => import('@/modules/plugin-repo').then(m => ({ default: m.PluginAuditPanel })))

// 检测任务模块
const StreamDetectionPage = lazyRetry(() => import('@/modules/detection-task/pages/StreamDetectionPage'))
const StreamDetectionDetailPage = lazyRetry(() => import('@/modules/detection-task/pages/StreamDetectionDetailPage'))
const PiExecutionPage = lazyRetry(() => import('@/modules/detection-task/pages/PiExecutionPage'))

// AIDD-OP 动态验证工作台模块
const AiddOpPage = lazyRetry(() => import('@/modules/aidd-op/pages/AiddOpPage').then(m => ({ default: m.AiddOpPage })))

// 基于API设计的新页面
const DocumentTaskListPage = lazyRetry(() => import('@/modules/detection-task/pages/DocumentTaskListPage'))
const DocumentTaskDetailPage = lazyRetry(() => import('@/modules/detection-task/pages/DocumentTaskDetailPage'))
const IssueAnalysisPage = lazyRetry(() => import('@/modules/detection-task/pages/IssueAnalysisPage'))

// 检测任务原型页面
const TaskListPrototypePage = lazyRetry(() => import('@/modules/detection-task/pages/TaskListPrototypePage'))
const TaskDetailPrototypePage = lazyRetry(() => import('@/modules/detection-task/pages/TaskDetailPrototypePage'))
const DocumentDetailPrototypePage = lazyRetry(() => import('@/modules/detection-task/pages/DocumentDetailPrototypePage'))

// 报告管理模块
const ReportListPage = lazyRetry(() => import('@/modules/report-management').then(m => ({ default: m.ReportListPage })))
const ReportTaskDetailPage = lazyRetry(() => import('@/modules/report-management').then(m => ({ default: m.ReportTaskDetailPage })))
const QualityReportPage = lazyRetry(() => import('@/modules/report-management').then(m => ({ default: m.QualityReportPage })))
const ProjectReportDetailPage = lazyRetry(() => import('@/modules/report-management/pages/ProjectReportDetailPage'))
const DynamicReportListPage = lazyRetry(() => import('@/modules/report-management/pages/DynamicReportListPage'))

// 问题管理模块
const IssueManagementTabsPage = lazyRetry(() => import('@/modules/issue-management/pages/IssueManagementTabsPage'))

// 系统工具页面
const SystemMonitorPage = lazyRetry(() => import('@/pages/SystemMonitorPage'))
const NotificationsPage = lazyRetry(() => import('@/pages/NotificationsPage'))
const AboutPage = lazyRetry(() => import('@/pages/AboutPage'))

// 规则验证模块 (已升级为增强版)
const RuleValidationPage = lazyRetry(() => import('@/modules/rule-validation/pages/RuleValidationPage').then(m => ({ default: m.RuleValidationPage })))
const ValidationDetectingPage = lazyRetry(() => import('@/modules/rule-validation/pages/ValidationDetectingPage').then(m => ({ default: m.ValidationDetectingPage })))
const ValidationReportPage = lazyRetry(() => import('@/modules/rule-validation/pages/ValidationReportPage').then(m => ({ default: m.ValidationReportPage })))

// 运维管理模块
const OperationsManagementPage = lazyRetry(() => import('@/modules/operations-management/pages/OperationsManagementPage'))

// 产品管理模块（领域 → 产品 → 版本 树，复用 fm_ 表）
const ProductManagementPage = lazyRetry(() => import('@/modules/product-management/pages/ProductManagementPage'))
const DomainTreePage = lazyRetry(() => import('@/modules/product-management/pages/DomainTreePage'))

// 文档页面
const DocsPage = lazyRetry(() => import('@/pages/DocsPage'))

// 评测模块
const EvaluationOverviewPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.EvaluationOverviewPage })))
const SamplesPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.SamplesPage })))
const DatasetsPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.DatasetsPage })))
const DatasetManagementPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.DatasetManagementPage })))
const RunsPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.RunsPage })))
const EvaluationReportPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.EvaluationReportPage })))
const ComparisonPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.ComparisonPage })))
const ComparisonReportPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.ComparisonReportPage })))
const GroundTruthPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.GroundTruthPage })))
const EditorPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.EditorPage })))
const RuleStatisticsPage = lazyRetry(() => import('@/modules/evaluation-module').then(m => ({ default: m.RuleStatisticsPage })))

// 加载中组件
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#3b82f6'
  }}>
    加载中...
  </div>
)

// 路由配置
export const routes: RouteObject[] = [
  // 文档路由
  {
    path: '/docs',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DocsPage />
      </Suspense>
    ),
  },
  {
    path: '/docs/*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DocsPage />
      </Suspense>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <RegisterPage />
      </Suspense>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/third-login/callback',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <OAuthCallbackPage />
      </Suspense>
    ),
  },

  // 受保护路由
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'projects',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DomainTreePage />
          </Suspense>
        ),
      },
      {
        path: 'projects-list',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProjectsPage />
          </Suspense>
        ),
      },
      {
        path: 'projects/domain/:domainId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DomainTreePage />
          </Suspense>
        ),
      },
      {
        path: 'projects/personal',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DomainTreePage />
          </Suspense>
        ),
      },
      {
        path: 'projects/personal/:entryId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DomainTreePage />
          </Suspense>
        ),
      },
      {
        path: 'projects/personal/:entryId/:focusedGroupId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DomainTreePage />
          </Suspense>
        ),
      },
      {
        path: 'project/:projectId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProjectDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'projects/:projectId/children',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SubProjectsPage />
          </Suspense>
        ),
      },
      {
        path: 'files',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FileManagementPage />
          </Suspense>
        ),
      },
      {
        path: 'file/:fileId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FileDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'chunk/:chunkId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ChunkDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'search',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SearchPage />
          </Suspense>
        ),
      },
      // 检测配置路由
      {
        path: 'detection-config',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DetectionConfigPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/rules',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DetectionRulesPage />
          </Suspense>
        ),
      },
      // 规则表单路由：已改为弹窗交互，旧独立页面路由重定向到列表页
      {
        path: 'detection-config/rules/create',
        element: <Navigate to="/detection-config?tab=rules" replace />,
      },
      {
        path: 'detection-config/rules/:ruleId',
        element: <Navigate to="/detection-config?tab=rules" replace />,
      },
      {
        path: 'detection-config/rules/:ruleId/edit',
        element: <Navigate to="/detection-config?tab=rules" replace />,
      },
      {
        path: 'detection-config/strategies',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DetectionStrategiesPage />
          </Suspense>
        ),
      },
      // 策略表单路由：已改为弹窗交互，旧独立页面路由重定向到列表页
      {
        path: 'detection-config/strategies/create',
        element: <Navigate to="/detection-config?tab=strategies" replace />,
      },
      {
        path: 'detection-config/strategies/:strategyId',
        element: <Navigate to="/detection-config?tab=strategies" replace />,
      },
      {
        path: 'detection-config/strategies/:strategyId/edit',
        element: <Navigate to="/detection-config?tab=strategies" replace />,
      },
      {
        path: 'detection-config/models',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ModelConfigsPage />
          </Suspense>
        ),
      },
      // 模型表单路由：已改为弹窗交互，旧独立页面路由重定向到列表页（test 路由保留）
      {
        path: 'detection-config/models/create',
        element: <Navigate to="/detection-config?tab=models" replace />,
      },
      {
        path: 'detection-config/models/:modelId',
        element: <Navigate to="/detection-config?tab=models" replace />,
      },
      {
        path: 'detection-config/models/:modelId/edit',
        element: <Navigate to="/detection-config?tab=models" replace />,
      },
      {
        path: 'detection-config/models/:modelId/test',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ModelTestPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/categories',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <CategoryManagementPage />
          </Suspense>
        ),
      },
      // SSH 目标管理路由
      {
        path: 'detection-config/ssh-targets',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SshTargetManagementPage />
          </Suspense>
        ),
      },
      // 模型标签管理路由
      {
        path: 'detection-config/model-tags',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ModelTagManagementPage />
          </Suspense>
        ),
      },
      // 链接版本号管理路由
      {
        path: 'detection-config/link-versions',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LinkVersionsPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/link-versions/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LinkVersionFormPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/link-versions/:versionId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LinkVersionFormPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/link-versions/:versionId/edit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <LinkVersionFormPage />
          </Suspense>
        ),
      },
      // 敏感词/不规范用语管理路由
      {
        path: 'detection-config/sensitive-words',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SensitiveWordsPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/sensitive-words/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SensitiveWordFormPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/sensitive-words/:wordId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SensitiveWordFormPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-config/sensitive-words/:wordId/edit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SensitiveWordFormPage />
          </Suspense>
        ),
      },
      // Plugin 仓库路由
      {
        path: 'plugin-repo',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PluginListPage />
          </Suspense>
        ),
      },
      {
        path: 'plugin-repo/:skillId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PluginDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'plugin-repo/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PluginFormPage />
          </Suspense>
        ),
      },
      {
        path: 'plugin-repo/:skillId/edit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PluginDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'plugin-repo/audit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PluginAuditPanel />
          </Suspense>
        ),
      },
      // 检测任务路由 - 使用新的API设计页面
      {
        path: 'detection-tasks',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DocumentTaskListPage />
          </Suspense>
        ),
      },
      {
        path: 'detection-tasks/:taskId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DocumentTaskDetailPage />
          </Suspense>
        ),
      },
      // Pi Agent 执行流程页面路由（Skill Agent 执行总览 + 问题）
      {
        path: 'detection-tasks/:taskId/pi-execution',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PiExecutionPage />
          </Suspense>
        ),
      },
      // AIDD-OP 动态验证工作台路由
      {
        path: 'aidd-op/:threadId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AiddOpPage />
          </Suspense>
        ),
      },
      // AIDD-OP 动态验证工作台路由（从检测任务详情跳转，带 taskId）
      {
        path: 'aidd-op/task/:taskId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AiddOpPage />
          </Suspense>
        ),
      },
      {
        path: 'stream-detection',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <StreamDetectionPage />
          </Suspense>
        ),
      },
      {
        path: 'stream-detection-detail',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <StreamDetectionDetailPage />
          </Suspense>
        ),
      },
      // 检测任务原型页面路由
      {
        path: 'detection-tasks-proto',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TaskListPrototypePage />
          </Suspense>
        ),
      },
      {
        path: 'detection-tasks-proto/:taskId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TaskDetailPrototypePage />
          </Suspense>
        ),
      },
      {
        path: 'detection-tasks-proto/:taskId/documents/:documentId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DocumentDetailPrototypePage />
          </Suspense>
        ),
      },
      // 报告管理路由
      {
        path: 'reports',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ReportListPage />
          </Suspense>
        ),
      },
      {
        path: 'reports/projects/:projectId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProjectReportDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'reports/tasks/:taskId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ReportTaskDetailPage />
          </Suspense>
        ),
      },
      {
        path: 'reports/tasks/:taskId/report',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <QualityReportPage />
          </Suspense>
        ),
      },
      {
        path: 'reports/tasks/:taskId/issues/:issueId?',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <IssueAnalysisPage />
          </Suspense>
        ),
      },
      // 动态验证报告列表路由
      {
        path: 'reports/dynamic',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DynamicReportListPage />
          </Suspense>
        ),
      },
      // 全局问题列表路由
      {
        path: 'issues',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <IssueManagementTabsPage />
          </Suspense>
        ),
      },
      // 系统工具路由
      {
        path: 'system-monitor',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SystemMonitorPage />
          </Suspense>
        ),
      },
      {
        path: 'notifications',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      // 规则验证路由 (已升级为增强版)
      {
        path: 'rule-validation',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RuleValidationPage />
          </Suspense>
        ),
      },
      {
        path: 'rule-validation/:taskId/detecting',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ValidationDetectingPage />
          </Suspense>
        ),
      },
      {
        path: 'rule-validation/:taskId/report',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ValidationReportPage />
          </Suspense>
        ),
      },
      // 运维管理路由
      {
        path: 'operations-management',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OperationsManagementPage />
          </Suspense>
        ),
      },
      // 产品管理路由（领域 → 产品 → 版本 树）
      {
        path: 'product-management',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProductManagementPage />
          </Suspense>
        ),
      },
      // 关于页面（入口：侧边栏用户下拉菜单，所有登录用户可见）
      {
        path: 'about',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AboutPage />
          </Suspense>
        ),
      },
      // 用户管理路由
      {
        path: 'profile',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        path: 'api-keys',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ApiKeysPage />
          </Suspense>
        ),
      },
      {
        path: 'teams',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <TeamsPage />
          </Suspense>
        ),
      },
      {
        path: 'user-management',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <UserManagementPage />
          </Suspense>
        ),
      },
      {
        path: 'oauth-providers',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OAuthProvidersPage />
          </Suspense>
        ),
      },
      {
        path: 'oauth-providers/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OAuthProviderFormPage />
          </Suspense>
        ),
      },
      {
        path: 'oauth-providers/:id/edit',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <OAuthProviderFormPage />
          </Suspense>
        ),
      },
      // 反馈管理路由
      {
        path: 'feedback',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <UserFeedbackPage />
          </Suspense>
        ),
      },
      {
        path: 'admin/feedback',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AdminFeedbackManagementPage />
          </Suspense>
        ),
      },
      // 评测模块路由（仅管理员可见）
      {
        path: 'evaluation/overview',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EvaluationOverviewPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/samples',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <SamplesPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/datasets',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DatasetsPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/datasets/:datasetId/manage',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DatasetManagementPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/runs',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RunsPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/reports/:runId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EvaluationReportPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/comparison',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ComparisonPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/comparison-reports/:comparisonId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ComparisonReportPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/samples/:sampleId/ground-truth',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <GroundTruthPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/samples/:sampleId/editor',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <EditorPage />
          </Suspense>
        ),
      },
      {
        path: 'evaluation/rule-statistics',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RuleStatisticsPage />
          </Suspense>
        ),
      },
    ],
  },
]
